import React, { useState, useEffect } from "react";
import Table from "../../core/pagination/datatable";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { DatePicker, InputNumber, Row, Col, Card } from "antd";
import { getProductDashboardData } from "../../core/api/products";
import { getProductOrderPrediction } from "../../core/api/mediaplan";
import { get100DaysROR } from "../../core/api/report";

const { RangePicker } = DatePicker;

const ProductOrdersSummary = () => {
  const selectedClients = useSelector((state) => state.selectedClients);
  const [productData, setProductData] = useState([]);
  const [rorData, setRorData] = useState({});
  const [campaignsByVariation, setCampaignsByVariation] = useState({});
  const [lagDays, setLagDays] = useState(5);
  const [dateRange, setDateRange] = useState([dayjs(), dayjs().add(90, "day")]);

  const [loading, setLoading] = useState(false);
  const handleDateChange = (dates) => {
    setDateRange(dates);
  };

  const handleLagDaysChange = (value) => {
    setLagDays(value);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        if (!selectedClients?.length) return;

        const startDate = dateRange[0].format("YYYY-MM-DD");
        const endDate = dateRange[1].format("YYYY-MM-DD");

        const [dashboardRes, predictionRes, rorRes] = await Promise.all([
          getProductDashboardData(selectedClients, startDate, endDate),
          getProductOrderPrediction(selectedClients),
          get100DaysROR(selectedClients),
        ]);

        const dashboard = dashboardRes.data;
        const prediction = predictionRes.data;
        const ror = rorRes.data;

        setRorData(ror);

        const groupedByVariation = {};
        prediction.forEach((item) => {
          const pv = item.product_variation_code;
          if (!groupedByVariation[pv]) groupedByVariation[pv] = [];
          groupedByVariation[pv].push(item);
        });

        setCampaignsByVariation(groupedByVariation);

        const today = dayjs().subtract(lagDays, "day");
        const from = dateRange[0];
        const to = dateRange[1];

        const enhanced = dashboard.map((product) => {
          const variationCampaigns =
            groupedByVariation[product.productVariation] || [];

          let totalQtyNeeded = 0;
          let totalOrders = 0;
          let qtyNeededUntilStart = 0;
          let ordersUntilStart = 0;

          variationCampaigns.forEach((item) => {
            const mailDate = dayjs(item.mail_date);
            let current = today;

            while (current.isSameOrBefore(to, "day")) {
              const dayDiff = current.diff(mailDate, "day");

              if (dayDiff >= 0 && dayDiff <= 100) {
                const rorValue = ror[dayDiff] || 0;
                const qty =
                  (((item.target_mail_qty || 0) * (item.estimated_ror || 0)) /
                    100) *
                  (rorValue / 100);
                const estimatedQty = qty * item.qty_per_order;

                if (current.isBefore(from, "day")) {
                  ordersUntilStart += qty;
                  qtyNeededUntilStart += estimatedQty;
                } else {
                  totalQtyNeeded += estimatedQty;
                  totalOrders += qty;
                }
              }

              current = current.add(1, "day");
            }
          });

          totalQtyNeeded = Math.ceil(totalQtyNeeded);
          totalOrders = Math.ceil(totalOrders);
          qtyNeededUntilStart = Math.ceil(qtyNeededUntilStart);
          ordersUntilStart = Math.ceil(ordersUntilStart);

          const stockToday = product.stockQty || 0;
          const stockOnStartDate = stockToday - qtyNeededUntilStart;

          const predictedStock =
            stockOnStartDate + (product.qtyOnDeliveryWithinRange || 0);
          const predictedMinusNeeded = predictedStock - totalQtyNeeded;
          const qtyToPurchase =
            predictedMinusNeeded < 0 ? Math.abs(predictedMinusNeeded) : 0;

          return {
            ...product,
            ordersUntilStart,
            qtyNeededUntilStart,
            totalQtyNeeded,
            totalOrders,
            stockQty: stockToday,
            stockOnStartDate,
            predictedStock,
            predictedStockMinusNeeded: predictedMinusNeeded,
            qtyToPurchase,
          };
        });

        // Sort by qty to purchase from highest to lowest
        const sortedData = enhanced
          .filter((item) => item.qtyToPurchase > 0)
          .sort((a, b) => b.qtyToPurchase - a.qtyToPurchase);

        setProductData(sortedData);
        setLoading(false);
      } catch (error) {
        console.error(
          "Error fetching data for OrdersSummary component:",
          error
        );
      }
    };

    fetchData();
  }, [selectedClients, dateRange, lagDays]);

  const columnsSecondary = [
    { title: "Brand", dataIndex: "brand", key: "brand" },
    { title: "Product Name", dataIndex: "productName", key: "productName" },
    {
      title: "Product Variation",
      dataIndex: "productVariation",
      key: "productVariation",
      fixed: "left",
    },
    {
      title: "Qty in Stock Today",
      dataIndex: "stockQty",
      key: "stockQty",
      width: 90,
    },
    {
      title: "Qty in Stock on Start Date",
      dataIndex: "stockOnStartDate",
      key: "stockOnStartDate",
      width: 90,
    },
    {
      title: `In Range: ${dateRange[0].format(
        "YYYY-MM-DD"
      )} - ${dateRange[1].format("YYYY-MM-DD")}`,
      children: [
        {
          title: "Total Orders",
          dataIndex: "totalOrders",
          key: "totalOrders",
          width: 160,
        },
        {
          title: "Total Qty Needed",
          dataIndex: "totalQtyNeeded",
          key: "totalQtyNeeded",
          width: 180,
        },
        {
          title: "Qty on Delivery",
          dataIndex: "qtyOnDeliveryWithinRange",
          key: "qtyOnDeliveryWithinRange",
          width: 150,
        },
      ],
    },
    {
      title: "Predicted Stock",
      dataIndex: "predictedStock",
      key: "predictedStock",
      width: 90,
    },
    {
      title: "Predicted Stock - Needed",
      dataIndex: "predictedStockMinusNeeded",
      key: "predictedStockMinusNeeded",
      width: 90,
      render: (value) => (
        <span style={{ color: value < 0 ? "red" : "inherit" }}>{value}</span>
      ),
    },
    {
      title: "Qty to Purchase",
      dataIndex: "qtyToPurchase",
      key: "qtyToPurchase",
      width: 90,
      render: (value) => (
        <span style={{ color: value > 0 ? "red" : "inherit" }}>{value}</span>
      ),
    },
  ];

  return (
    <div>
      <div className="d-flex items-center">
        <label className="text-lg font-semibold whitespace-nowrap m-2">
          Lag (Days):
        </label>
        <InputNumber
          min={0}
          value={lagDays}
          onChange={handleLagDaysChange}
          style={{ width: 100, marginRight: 20 }}
        />

        <label className="col-1 text-lg font-semibold whitespace-nowrap m-2">
          Date Range:
        </label>
        <RangePicker
          value={dateRange}
          onChange={handleDateChange}
          allowClear={false}
          style={{ width: 575 }}
        />
      </div>

      <Table
        columns={columnsSecondary}
        dataSource={productData}
        scroll={{ x: "max-content" }}
        loading={loading}
      />
    </div>
  );
};

export default ProductOrdersSummary;
